/** First-party UX telemetry for login, funnels, uploads, and short mobile sessions. */

export type UxProduct = 'website' | 'console' | 'pursuit' | 'vantage' | 'inlet' | 'razzmatazz';

export type UxEventType =
  | 'login_failed'
  | 'upload_failed'
  | 'funnel_step'
  | 'funnel_abandoned'
  | 'funnel_completed'
  | 'session_short'
  | 'rage_click'
  | 'dead_click'
  | 'client_error'
  | 'product_error'
  | 'vantage_job_failed'
  | 'research_job_abandoned';

export type UxEventInput = {
  event_type: UxEventType;
  product?: UxProduct;
  funnel_key?: string;
  step?: string;
  last_path?: string;
  email?: string;
  user_id?: string;
  device_class?: 'desktop' | 'tablet' | 'mobile' | 'unknown';
  dwell_ms?: number;
  error_code?: string;
  message?: string;
  payload?: Record<string, unknown>;
};

const SESSION_KEY = 'nextcompli.ux.session';
const SHORT_SESSION_MS = 15000;
const RAGE_CLICK_WINDOW_MS = 1000;
const RAGE_CLICK_COUNT = 3;
const INTERACTIVE_SELECTOR = 'a,button,input,select,textarea,label,summary,option,[role="button"],[role="link"],[role="tab"],[contenteditable="true"]';

let ingestUrl = '';
let installed = false;
let pageEnteredAt = 0;
let lastPath = '';
let lastFunnel: { key: string; step: string } | null = null;
let clickStamps: { x: number; y: number; at: number }[] = [];

const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    if (/^https?:\/\/localhost(?::|\/|$)/i.test(configured)) {
      return configured.replace(/localhost/i, '127.0.0.1').replace(/\/$/, '');
    }
    return configured.replace(/\/$/, '');
  }
  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000';
  }
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return 'http://127.0.0.1:8000';
  }
  const apexHost = host.replace(/^www\./, '').replace(/^dashboard\./, '').replace(/^metrics\./, '');
  return `https://${apexHost}`;
};

export function classifyDeviceClass(width = 0, userAgent = ''): 'desktop' | 'tablet' | 'mobile' | 'unknown' {
  if (width > 0 && width <= 767) return 'mobile';
  if (width > 0 && width <= 1024) return 'tablet';
  const ua = userAgent.toLowerCase();
  if (/ipad|tablet/.test(ua)) return 'tablet';
  if (/iphone|android|mobile|iemobile/.test(ua)) return 'mobile';
  if (ua || width > 1024) return 'desktop';
  return 'unknown';
}

export function productFromPath(pathname: string): UxProduct {
  if (pathname.startsWith('/console/pursuit') || pathname.startsWith('/console/crm')) return 'pursuit';
  if (pathname.startsWith('/console/inlet')) return 'inlet';
  if (pathname.startsWith('/console/razzmatazz')) return 'razzmatazz';
  if (pathname.startsWith('/console/research')) return 'vantage';
  if (pathname.startsWith('/console')) return 'console';
  return 'website';
}

export function isDeadClickTarget(target: EventTarget | null): boolean {
  if (!(typeof Element !== 'undefined' && target instanceof Element)) {
    return false;
  }
  if (target.closest('button[disabled], [aria-disabled="true"]')) {
    return true;
  }
  if (target.closest(INTERACTIVE_SELECTOR)) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.getComputedStyle(target).cursor === 'pointer';
  } catch {
    return false;
  }
}

export function sessionId(): string {
  if (typeof window === 'undefined') {
    return 'server';
  }
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      return existing;
    }
    const next = `ux_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return `ux_${Date.now().toString(36)}`;
  }
}

export function markFunnel(key: string, step: string): void {
  lastFunnel = { key, step };
}

export function clearFunnel(): void {
  lastFunnel = null;
}

const telemetryEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (process.env.NODE_ENV === 'test' && process.env.REACT_APP_UX_TELEMETRY !== '1') {
    return false;
  }
  return true;
};

export function trackUxEvent(input: UxEventInput): void {
  if (!telemetryEnabled()) {
    return;
  }
  const path = input.last_path || window.location.pathname;
  const body = {
    event_type: input.event_type,
    product: input.product || productFromPath(path),
    funnel_key: input.funnel_key,
    step: input.step,
    last_path: path,
    session_id: sessionId(),
    email: input.email,
    user_id: input.user_id,
    device_class: input.device_class || classifyDeviceClass(window.innerWidth, window.navigator.userAgent),
    dwell_ms: input.dwell_ms,
    error_code: input.error_code,
    message: input.message,
    payload: input.payload,
  };
  const url = ingestUrl || `${inferGatewayBaseUrl()}/api/v1/ux/events`;
  const payload = JSON.stringify(body);
  try {
    if (input.event_type === 'session_short' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    /* fall through to fetch */
  }
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
}

const maybeRageClick = (event: MouseEvent) => {
  const now = Date.now();
  clickStamps = clickStamps.filter((stamp) => now - stamp.at <= RAGE_CLICK_WINDOW_MS);
  clickStamps.push({ x: event.clientX, y: event.clientY, at: now });
  const clustered = clickStamps.filter(
    (stamp) => Math.abs(stamp.x - event.clientX) < 24 && Math.abs(stamp.y - event.clientY) < 24,
  );
  if (clustered.length >= RAGE_CLICK_COUNT) {
    clickStamps = [];
    trackUxEvent({
      event_type: 'rage_click',
      step: lastFunnel?.step,
      funnel_key: lastFunnel?.key,
      message: 'Repeated clicks in the same spot',
    });
    return;
  }
  if (isDeadClickTarget(event.target)) {
    trackUxEvent({
      event_type: 'dead_click',
      step: lastFunnel?.step,
      funnel_key: lastFunnel?.key,
      message: 'Click on a non-interactive control',
    });
  }
};

const captureClientError = (errorCode: string, message: string) => {
  trackUxEvent({
    event_type: 'client_error',
    funnel_key: lastFunnel?.key,
    step: lastFunnel?.step,
    last_path: lastPath || (typeof window !== 'undefined' ? window.location.pathname : ''),
    error_code: errorCode,
    message: String(message || 'client_error').slice(0, 500),
  });
};

const flushShortSession = () => {
  const dwell = Date.now() - pageEnteredAt;
  const device = classifyDeviceClass(window.innerWidth, window.navigator.userAgent);
  if (dwell >= SHORT_SESSION_MS || (device !== 'mobile' && device !== 'tablet')) {
    return;
  }
  trackUxEvent({
    event_type: 'session_short',
    funnel_key: lastFunnel?.key,
    step: lastFunnel?.step,
    last_path: lastPath || window.location.pathname,
    dwell_ms: dwell,
    device_class: device,
  });
};

export function installUxTelemetry(gatewayBaseUrl?: string): void {
  if (!telemetryEnabled() || installed) {
    return;
  }
  installed = true;
  ingestUrl = `${(gatewayBaseUrl || inferGatewayBaseUrl()).replace(/\/$/, '')}/api/v1/ux/events`;
  pageEnteredAt = Date.now();
  lastPath = window.location.pathname;
  window.addEventListener('click', maybeRageClick, true);
  window.addEventListener('error', (event) => {
    captureClientError('window_error', (event as ErrorEvent).message || 'window_error');
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    captureClientError('unhandledrejection', String(reason?.message || reason || 'unhandledrejection'));
  });
  window.addEventListener('pagehide', flushShortSession);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushShortSession();
    }
  });
}

export function noteLocation(pathname: string): void {
  lastPath = pathname;
  pageEnteredAt = Date.now();
}

export function _resetUxTelemetryForTests(): void {
  installed = false;
  ingestUrl = '';
  lastFunnel = null;
  clickStamps = [];
  pageEnteredAt = 0;
}
